/**
 * `window.crucible` shim — backwards-compat for the 244 lifted-from-
 * Electron callsites in `@volley/crucible-ui` that still call into
 * the Electron preload bridge by way of the `window.crucible` global.
 *
 * BG-1 lifted the renderer tree to `@volley/crucible-ui` but
 * preserved the `window.crucible.invoke(...)` call shape so the
 * Electron build kept passing behavioural-equivalence. BG-2 / BG-3 /
 * BG-5 then introduced `CrucibleClient` / `BrowserCrucibleClient` so
 * lifted code could eventually migrate to `useCrucibleClient()`.
 *
 * That per-callsite migration is a multi-PR effort. BG-5.2 ships
 * this shim as a stepping stone: when the browser SPA reaches
 * `phase: 'ready'`, we install a `window.crucible` whose `invoke`
 * forwards directly to the active client. The lifted App + every
 * page + every nested component continues to work with zero edits.
 *
 * The shim is intentionally narrow: it implements only the two
 * methods the preload bridge exposes (`invoke` + `on`). Any callsite
 * that reaches for an Electron-only window global (e.g.
 * `window.crucible.openExternal`) crashes loudly — surfacing the
 * migration debt where it actually matters.
 *
 * Cross-references:
 *   - `packages/crucible-gui/src/preload/window.d.ts` — preload-side
 *     surface that the shim mirrors
 *   - `packages/crucible-ui/src/transport/CrucibleClient.ts` — what
 *     the shim's invoke + on delegate to
 *   - `docs/browser-gui/post-bg-6-status.md` — gates this PR unlocks
 */

import type {
  CrucibleClient,
  InvokeChannel,
  PushChannel,
} from '@volley/crucible-client/CrucibleClient.js';
import { CrucibleError } from '@volley/crucible-client/error-mapping.js';
import type {
  InvokeChannelMap,
  PushChannelMap,
} from '@volley/crucible-ipc-types';

/**
 * Brand used to distinguish a post-teardown shim from a live one
 * (codex round-1 S4). Non-enumerable so it doesn't leak into
 * downstream serialisation paths (e.g. structuredClone, JSON.stringify
 * if a debug surface ever stringifies the shim).
 */
const POST_TEARDOWN_BRAND = Symbol.for('crucible-web/post-teardown-shim');

/**
 * Shape matches `CrucibleApi` from `@volley/crucible-ui/src/window.d.ts`
 * exactly so TypeScript's interface merging accepts both declarations.
 * If the canonical `CrucibleApi` ever widens (e.g. to add the
 * optional InvokeOptions slot), update this shim in lockstep.
 */
interface WindowCrucibleApi {
  invoke<C extends InvokeChannel>(
    channel: C,
    ...args: InvokeChannelMap[C][0] extends void
      ? []
      : [InvokeChannelMap[C][0]]
  ): Promise<InvokeChannelMap[C][1]>;
  on<E extends PushChannel>(
    event: E,
    callback: (data: PushChannelMap[E]) => void,
  ): () => void;
}

// The `window.crucible` global is already declared as non-optional
// in `@volley/crucible-ui/src/window.d.ts` (`CrucibleApi`). The
// shapes are structurally identical so the declarations merge
// cleanly; no separate `declare global` needed on this side.

/**
 * Install the shim on `window.crucible`. Returns an `uninstall` fn
 * that removes the shim — used during teardown (logout, bfcache
 * eviction, sign-out) so a stale client doesn't outlive its
 * lifecycle.
 *
 * Calling twice is fine; the second call replaces the first, and
 * the first call's uninstall becomes a no-op (won't accidentally
 * remove the new shim).
 */
export function installWindowCrucibleShim(
  client: CrucibleClient,
): () => void {
  const shim: WindowCrucibleApi = {
    invoke: ((channel: InvokeChannel, ...args: unknown[]): Promise<unknown> =>
      (client.invoke as (c: InvokeChannel, ...a: unknown[]) => Promise<unknown>)(
        channel,
        ...args,
      )) as WindowCrucibleApi['invoke'],
    on: ((event: PushChannel, handler: (payload: unknown) => void): (() => void) =>
      client.subscribe(event, handler as never)) as WindowCrucibleApi['on'],
  };
  window.crucible = shim;
  return () => {
    // Restore by replacing with an undefined-equivalent. The
    // canonical declaration is non-optional so `delete` is invalid;
    // for teardown we assign a thin shim that rejects to surface
    // the lifecycle error if any callsite reaches the global after
    // logout / unmount.
    if (window.crucible === shim) {
      window.crucible = makePostTeardownShim();
    }
  };
}

function makePostTeardownShim(): WindowCrucibleApi {
  // codex round-1 S3: typed CrucibleError with a transport-teardown
  // code so error handlers + Sentry breadcrumbs can branch on the
  // code rather than regex-matching a message string. Distinguishes
  // a normal logout / phase-change teardown from a genuine
  // "lifecycle never installed" violation.
  const teardownError = (): CrucibleError =>
    new CrucibleError({
      name: 'CrucibleError',
      message:
        'transport torn down — window.crucible inactive (logout or session lost)',
      code: 'CRUCIBLE-499',
      category: 'network',
      recovery:
        'Sign in again. In-flight requests during teardown will be retried after the next boot.',
      retryable: true,
    });
  const shim: WindowCrucibleApi = {
    invoke: (() =>
      Promise.reject(teardownError())) as WindowCrucibleApi['invoke'],
    on: (() => () => undefined) as WindowCrucibleApi['on'],
  };
  // Brand the shim so isWindowCrucibleShimInstalled can distinguish.
  Object.defineProperty(shim, POST_TEARDOWN_BRAND, {
    value: true,
    enumerable: false,
    writable: false,
    configurable: false,
  });
  return shim;
}

/**
 * Test seam: check whether a LIVE shim is installed (returns false
 * for both "never installed" AND "installed-then-uninstalled"
 * states). Distinguishes via the post-teardown brand (codex
 * round-1 S4).
 */
export function isWindowCrucibleShimInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const c = (window as { crucible?: WindowCrucibleApi }).crucible;
  if (c === undefined) return false;
  return !(c as unknown as Record<symbol, unknown>)[POST_TEARDOWN_BRAND];
}
